// @LTG-ID: data/journals/add-backs-selling-a-business
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 18 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "add-backs-selling-a-business",
    FRONTMATTER: {
        number: "55",
        section: "Valuation",
        sectionHref: "/journals#core-valuation",
        sectionAnchor: "core-valuation",
        title: "Add-Backs Explained: How Normalized Earnings Are Calculated When Selling a Business",
        deck: "Add-backs are adjustments to a business's reported profit that remove expenses a new owner will not incur, such as the seller's excess compensation, personal expenses, and one-time costs.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-03-17",
        publishedHuman: "March 17, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 417,
        timeRequired: "PT3M",
        keywords: "add-backs selling a business, normalized earnings, EBITDA adjustments, owner add-backs business sale",
        articleSection: "Valuation",
        articleBodyPreview: "Add backs are adjustments to a business's reported profit that remove expenses a new owner will not incur, such as the seller's excess compensation, personal expenses, and one time costs. The result, called normalized or adjusted earnings, is the figure buyers apply multiples to. Every add back must be documented and defensible. The earnings number that sells a business is almost never the number on the tax return. Between the two sits the add back schedule, the most negotiated page in most smal",
        complianceNote: "This article is educational and does not constitute accounting or tax advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "what-is-ebitda",
        "sde-vs-ebitda-guide",
        "clean-financial-records-business-value"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Add-Backs: Normalized Earnings in a Business Sale · LTG",
        canonical: "https://letstalkgrowth.com/journals/add-backs-selling-a-business",
        ogImage: "/journals/og/add-backs-selling-a-business.jpg",
    },
    BODY_MARKDOWN: `Add-backs are adjustments to a business's reported profit that remove expenses a new owner will not incur, such as the seller's excess compensation, personal expenses, and one-time costs. The result, called normalized or adjusted earnings, is the figure buyers apply multiples to. Every add-back must be documented and defensible.

The earnings number that sells a business is almost never the number on the tax return. Between the two sits the add-back schedule, the most negotiated page in most small business deals.

## What legitimately gets added back

The test for a valid add-back is simple to state: would this expense exist for a rational new owner? Categories that routinely pass:

- **Owner compensation adjustments.** In an SDE presentation, the owner's full compensation is added back by definition. In an EBITDA presentation, only the excess above a market-rate replacement salary qualifies.
- **Discretionary and personal expenses.** The family vehicle, personal travel, health club, and relatives on payroll who do not work in the business.
- **One-time items.** A lawsuit settlement, a flood repair, a one-off consulting project, costs with a clear beginning and end.
- **Related-party pricing.** Rent paid to the owner's own real estate entity above or below market gets restated to market, in whichever direction honesty requires.

## What buyers push back on

Every experienced buyer has seen aggressive schedules, and the pushback follows patterns. Recurring "one-time" expenses that appear every year. Marketing or maintenance cut to zero in the final year to inflate profit, a move buyers read as deferred cost they will have to pay. Personal expenses claimed without documentation. And the fatal category: add-backs for unreported cash income, which cannot be added back because they were never reported, a point covered bluntly in our financial records article.

The credibility dynamic matters more than any single line item. A schedule with eight clean, documented adjustments earns trust; the same schedule padded with two indefensible ones invites the buyer to re-examine all ten. Overreaching on add-backs is one of the few mistakes that costs sellers more than the disputed amount itself.

## Preparing your schedule properly

Start early, ideally with the CPA who prepares your statements, and build the documentation trail as you go: invoices, contracts, and memos that support each adjustment. Present conservatively, holding back the arguable items rather than leading with them. A buyer who accepts your numbers quickly is a buyer moving toward closing; a buyer auditing your credibility is a buyer discovering reasons to retrade.`,
    FAQ: [
        { question: "What is the difference between add-backs and adjustments?", answer: "The terms are used interchangeably in practice. Both refer to normalizing reported profit to reflect the true earning power a new owner would receive, by removing non-recurring, personal, and non-market items." },
        { question: "Can I add back my own salary?", answer: "In an SDE presentation for an owner-operated business, yes, the owner's full compensation is added back by definition. In an EBITDA presentation, a market-rate management salary must remain in the expenses, and only compensation above that level is adjusted." },
        { question: "Who verifies add-backs?", answer: "The buyer's diligence team, and on larger deals a quality of earnings provider, will test every adjustment against source documents. Undocumented add-backs are typically disallowed in the buyer's analysis, which lowers the effective price." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "What Is EBITDA? A Plain-English Guide for Business Owners",
            meta: "3 min · Brief 40",
            to: "/journals/what-is-ebitda",
        },
        {
            tag: "Valuation",
            title: "SDE vs EBITDA: Which One Applies to Your Business?",
            meta: "3 min · Brief 41",
            to: "/journals/sde-vs-ebitda-guide",
        },
        {
            tag: "Value Drivers",
            title: "Why Clean Financial Records Are Worth Real Money When You Sell",
            meta: "3 min · Brief 51",
            to: "/journals/clean-financial-records-business-value",
        },
    ],
};

export default BRIEF;
